import { defineStore } from 'pinia'
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, Timestamp, updateDoc, getFirestore } from 'firebase/firestore'
import { getApp } from 'firebase/app'

export interface OrderLine {
  sku: string
  qty: number
  internalIdExportUse?: string | number
}

export interface Order {
  id: string
  timestamp: number
  lastUpdated: number
  userEmail: string
  status: string
  lines: OrderLine[]
  styles: (string | number)[]
  accountName?: string
  shippingAddress?: string
  requestedShipDate?: string
  requestedCancelDate?: string
  poNumber?: string
  workbook?: string
  dealerId?: string // Added in Phase 2 - assigned dealer ID
  lastEditedByUid?: string // Added in Phase 2 - UID of last editor
  lastEditedByEmail?: string // Added in Phase 2 - email of last editor
  lastEditedAt?: number // Added in Phase 2 - timestamp of last edit
}

const LS_KEY = 'orders-v1'
const DATABASE_ID = 'kudo-sales'
let firestoreInstanceLogged = false

function generateId() {
  return 'kudo-demo-dwb-' + Date.now() + '-' + Math.floor(Math.random() * 100000)
}

/**
 * Get Firestore instance with explicit database ID.
 * This ensures all operations use the correct 'kudo-sales' database.
 */
function getFirestoreInstance() {
  try {
    const app = getApp()
    const db = getFirestore(app, DATABASE_ID)
    if (import.meta.dev && !firestoreInstanceLogged) {
      console.log('[orders] Using Firestore database:', DATABASE_ID)
      firestoreInstanceLogged = true
    }
    return db
  } catch (error) {
    console.error('[orders] Failed to get Firestore instance:', error)
    throw new Error(`Failed to initialize Firestore with database '${DATABASE_ID}'`)
  }
}

export const useOrdersStore = defineStore('orders', {
  state: () => ({
    orders: [] as Order[]
  }),
  getters: {
    ordersForUser: (state) => (email: string) =>
      state.orders.filter(order => order.userEmail === email),
    latestOrder: (state) => (email: string) =>
      state.orders
        .filter(order => order.userEmail === email)
        .sort((a, b) => b.timestamp - a.timestamp)[0] || null,
    orderById: (state) => (id: string) =>
      state.orders.find(order => order.id === id) || null,
    allOrders: (state) => state.orders
  },
  actions: {
    async hydrate() {
      if (typeof window === 'undefined') return
      try {
        const raw = localStorage.getItem(LS_KEY)
        if (raw) {
          const parsed: Order[] = JSON.parse(raw)
          this.orders = Array.isArray(parsed) ? parsed : []
        }
      } catch {
        // ignore
      }
      await this.fetchOrdersFromFirestore()
    },
    persist() {
      if (typeof window === 'undefined') return
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(this.orders))
      } catch {
        // ignore
      }
    },
    async addOrder(order: Omit<Order, 'id' | 'timestamp' | 'lastUpdated'>) {
      const now = Date.now()
      const newOrder: Order = {
        ...order,
        id: generateId(),
        timestamp: now,
        lastUpdated: now,
        workbook: order.workbook || 'Demo Workbook'
      }
      this.orders.push(newOrder)
      this.persist()
      await this.saveOrderToFirestore(newOrder)
      return newOrder
    },
    async saveOrderToFirestore(order: Order) {
      try {
        const db = getFirestoreInstance()
        const orderClone = { ...order }
        orderClone.timestamp = Timestamp.fromMillis(order.timestamp)
        orderClone.lastUpdated = Timestamp.fromMillis(order.lastUpdated)
        await setDoc(doc(collection(db, 'orders'), order.id), orderClone)
        if (import.meta.dev) {
          console.log('[orders] Saved order to Firestore:', order.id)
        }
      } catch (error) {
        console.error('[orders] Failed to save order to Firestore:', error)
        throw error
      }
    },
    async fetchOrdersFromFirestore() {
      try {
        const db = getFirestoreInstance()
        let userEmail = null
        try {
          const { useAuthStore } = await import('@/store/auth')
          const auth = useAuthStore()
          userEmail = auth.user?.email || null
        } catch {}
        let q
        if (userEmail) {
          q = query(collection(db, 'orders'), where('userEmail', '==', userEmail))
          if (import.meta.dev) {
            console.log('[orders] Fetching orders for user:', userEmail)
          }
        } else {
          q = collection(db, 'orders')
          if (import.meta.dev) {
            console.log('[orders] Fetching all orders (no user filter)')
          }
        }
        const snapshot = await getDocs(q)
        const fetchedOrders: Order[] = []
        snapshot.forEach(docSnap => {
          const data = docSnap.data()
          fetchedOrders.push({
            ...data,
            timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toMillis() : data.timestamp,
            lastUpdated: data.lastUpdated instanceof Timestamp ? data.lastUpdated.toMillis() : data.lastUpdated ?? data.timestamp,
          })
        })
        // Always update orders from Firestore, even if empty, to clear stale localStorage data
        this.orders = fetchedOrders
        this.persist()
        if (import.meta.dev) {
          console.log('[orders] Fetched', fetchedOrders.length, 'orders from Firestore')
        }
      } catch (error) {
        console.error('[orders] Failed to fetch orders from Firestore:', error)
      }
    },
    async fetchAllOrdersFromFirestore() {
      try {
        const db = getFirestoreInstance()
        const q = collection(db, 'orders')
        if (import.meta.dev) {
          console.log('[orders] Fetching all orders from Firestore (admin mode)')
        }
        const snapshot = await getDocs(q)
        const fetchedOrders: Order[] = []
        snapshot.forEach(docSnap => {
          const data = docSnap.data()
          fetchedOrders.push({
            ...data,
            timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toMillis() : data.timestamp,
            lastUpdated: data.lastUpdated instanceof Timestamp ? data.lastUpdated.toMillis() : data.lastUpdated ?? data.timestamp,
          })
        })
        // Always update orders from Firestore, even if empty, to clear stale localStorage data
        this.orders = fetchedOrders
        this.persist()
        if (import.meta.dev) {
          console.log('[orders] Fetched', fetchedOrders.length, 'orders from Firestore (all orders)')
        }
      } catch (error) {
        console.error('[orders] Failed to fetch all orders from Firestore:', error)
      }
    },
    async deleteOrderFirestore(orderId: string) {
      try {
        const db = getFirestoreInstance()
        await deleteDoc(doc(collection(db, 'orders'), orderId))
        this.orders = this.orders.filter(o => o.id !== orderId)
        this.persist()
        if (import.meta.dev) {
          console.log('[orders] Deleted order from Firestore:', orderId)
        }
      } catch (error) {
        console.error('[orders] Failed to delete order from Firestore:', error)
        throw error
      }
    },
    async updateOrderStatus(orderId: string, status: string) {
      try {
        const db = getFirestoreInstance()
        const now = Date.now()
        await updateDoc(doc(collection(db, 'orders'), orderId), {
          status,
          lastUpdated: Timestamp.fromMillis(now)
        })
        const order = this.orders.find(o => o.id === orderId)
        if (order) {
          order.status = status
          order.lastUpdated = now
          this.persist()
        }
        if (import.meta.dev) {
          console.log('[orders] Updated order status:', orderId, 'to', status)
        }
      } catch (error) {
        console.error('[orders] Failed to update order status:', error)
        throw error
      }
    },
    async updateOrderField(orderId: string, field: string, value: string) {
      try {
        const db = getFirestoreInstance()
        const now = Date.now()
        const docRef = doc(collection(db, 'orders'), orderId)
        await updateDoc(docRef, { [field]: value, lastUpdated: Timestamp.fromMillis(now) })
        const order = this.orders.find(o => o.id === orderId)
        if (order) {
          // Type-safe assignment: only update if field exists on Order type
          if (field in order) {
            order[field as keyof Order] = value as any
          }
          order.lastUpdated = now
          this.persist()
        }
        if (import.meta.dev) {
          console.log('[orders] Updated order field:', orderId, field, '=', value)
        }
      } catch (error) {
        console.error('[orders] Failed to update order field:', error)
        throw error
      }
    },
    async updateOrder(orderId: string, updates: Partial<Order>) {
      try {
        const db = getFirestoreInstance()
        const now = Date.now()
        const docRef = doc(collection(db, 'orders'), orderId)
        await updateDoc(docRef, { ...updates, lastUpdated: Timestamp.fromMillis(now) })
        const order = this.orders.find(o => o.id === orderId)
        if (order) {
          Object.assign(order, updates)
          order.lastUpdated = now
          this.persist()
        }
        if (import.meta.dev) {
          console.log('[orders] Updated order:', orderId, Object.keys(updates))
        }
      } catch (error) {
        console.error('[orders] Failed to update order:', error)
        throw error
      }
    },
    clearOrdersForUser(email: string) {
      this.orders = this.orders.filter(order => order.userEmail !== email)
      this.persist()
    }
  }
})